import { User } from '@awork/features/user/models/user.model'
import type { FileUpload } from '@awork/_shared/models/file-upload.model'
import replace from '@awork/_shared/functions/replace'
import { UserQuery } from '@awork/features/user/state/user.query'
import type { ValidActivityLogEntities } from '@awork/_shared/services/activity-log-service/types'

export interface IMentions {
  description?: string
  users: User[]
  agentIds?: string[]
  isMentioningPersonalAgent?: boolean
  everyUserHadPermissions?: boolean
}

export interface Reaction {
  userId: string
  emoji: string
}

export interface GroupedReaction {
  userIds: string[]
  emoji: string
}

/**
 * The model type for comment
 */
export interface IComment {
  id: string
  entityId: string
  entityType?: 'tasks' | 'projects' | 'documents'
  entity?: {
    id: string
    name: string
  }
  attachments?: FileUpload[]
  hasAttachments?: boolean
  createdOn?: Date
  createdBy?: string
  updatedOn?: Date
  updatedBy?: string
  user?: User
  userId?: string
  agentId?: string
  agentNameSnapshot?: string
  message: string
  formattedMessage?: string
  plainFormattedMessage?: string
  mentions?: IMentions
  previews?: string[]
  editMessage?: string
  mode?: 'view' | 'edit'
  reactions?: Reaction[]
  isGrouped?: boolean
  isHiddenForConnectUsers?: boolean
  inReplyToCommentId?: string
  isResolved?: boolean
  project?: {
    id: string
    name: string
    projectTypeIcon?: string
    projectCompanyProfileImageUrl?: string
  }
}

/**
 * Comment model
 */
export class Comment implements IComment {
  id: string
  entityId: string
  entityType?: 'tasks' | 'projects' | 'documents'
  entity?: {
    id: string
    name: string
  }
  attachments?: FileUpload[]
  hasAttachments?: boolean
  createdOn?: Date
  createdBy?: string
  updatedOn?: Date
  updatedBy?: string
  user?: User
  userId?: string
  agentId?: string
  agentNameSnapshot?: string
  message: string
  formattedMessage?: string
  plainFormattedMessage?: string
  mentions?: IMentions
  previews?: string[]
  editMessage?: string
  mode?: 'view' | 'edit'
  project?: {
    id: string
    name: string
    projectTypeIcon?: string
    projectCompanyProfileImageUrl?: string
  }
  reactions?: Reaction[]
  isGrouped?: boolean
  isHiddenForConnectUsers?: boolean
  inReplyToCommentId?: string
  isResolved?: boolean

  readonly commentMessageMaxLength = 65000

  constructor(comment: IComment) {
    Object.assign(this, comment)

    if (!this.editMessage) {
      this.editMessage = this.message
    }

    if (!this.mode) {
      this.mode = 'view'
    }

    if (!this.attachments) {
      this.attachments = []
    }

    if (this.user) {
      this.user = new User(this.user, 'Comment')
    }
  }

  get messageHTML(): string {
    if (this.formattedMessage) {
      return this.formattedMessage.replace(/(?:\r\n|\r|\n)/g, '<br>')
    } else if (this.plainFormattedMessage) {
      return this.plainFormattedMessage.replace(/(?:\r\n|\r|\n)/g, '<br>')
    } else if (this.message) {
      return this.message.replace(/(?:\r\n|\r|\n)/g, '<br>')
    } else {
      return ''
    }
  }

  get name(): string {
    return q.translations.common.comment
  }

  /**
   * Updates the comment's reaction according to the operation
   * @param {string} reaction
   * @param {'add' | 'remove'} operation
   * @param {User} currentUser
   */
  updateReactions(reaction: string, operation: 'add' | 'remove', currentUser: User): void {
    switch (operation) {
      case 'add': {
        this.reactions = this.reactions || []

        const existingReaction = this.reactions.find(r => r.userId === currentUser.id && r.emoji === reaction)

        if (!existingReaction) {
          this.reactions.push({
            emoji: reaction,
            userId: currentUser.id
          })
        }

        break
      }

      case 'remove':
        this.reactions = this.reactions.filter(r => r.userId !== currentUser.id || r.emoji !== reaction)
    }
  }

  /**
   * Generates the reaction tooltip containing the user names
   * @param {GroupedReaction} groupedReaction
   * @param {ValidActivityLogEntities} entity
   * @param {boolean} [includeEmoji=true] True to include reaction emoji into the string
   * @returns {string}
   */
  getReactionTooltip(groupedReaction: GroupedReaction, entity: ValidActivityLogEntities, includeEmoji = true): string {
    if (!UserQuery.instance) {
      return null
    }

    void entity

    const currentUser = UserQuery.instance.getCurrentUser()
    const users = UserQuery.instance.getUsersByIds(groupedReaction.userIds, true)
    const notFound = groupedReaction.userIds.filter(userId => !users.some(user => user.id === userId)).length

    users.sort((userA, userB) => {
      const userAFullName = `${userA.firstName || ''} ${userA.lastName || ''}`.trim()
      const userBFullName = `${userB.firstName || ''} ${userB.lastName || ''}`.trim()

      if (!userAFullName && userBFullName) {
        return 1
      }

      if (userAFullName && !userBFullName) {
        return -1
      }

      if (!userAFullName && !userBFullName) {
        return 0
      }

      return userAFullName.localeCompare(userBFullName)
    })

    const currentUserIndex = users.findIndex(user => user.id === currentUser.id)

    // Put the current user at last position
    if (currentUserIndex >= 0) {
      users.push(
        users.splice(
          users.findIndex(user => user.id === currentUser.id),
          1
        )[0]
      )
    }

    const userNames: string[] = []

    // Show the users not found as 'n users reacted with..'
    if (notFound > 0) {
      const userTranslation = notFound > 1 ? q.translations.entities.users : q.translations.entities.user

      userNames.push(`${notFound} ${userTranslation.toLowerCase()}`)
    }

    users.forEach(user => {
      if (user.id === currentUser.id) {
        const you = q.translations.Comment.you

        userNames.push(userNames.length >= 1 ? you.toLowerCase() : you)
      } else {
        userNames.push(user.fullName)
      }
    })

    let userNamesString: string

    // Generate the users string as: User 1, User 2 and User 3...
    const userCount = userNames.length

    if (userNames.length === 1) {
      userNamesString = userNames[0]
    } else {
      const lastUser = userNames.pop()

      userNamesString = `${userNames.join(', ')} ${q.translations.common.and} ${lastUser}`
    }

    if (!includeEmoji) {
      return userNamesString
    }

    const onlyOneUserReacted = userCount === 1

    if (!onlyOneUserReacted) {
      return replace(q.translations.Comment.usersReactedWith, {
        users: userNamesString,
        reaction: groupedReaction.emoji
      })
    }

    const onlyUserIsCurrentUser = users[0]?.id === currentUser.id

    if (onlyUserIsCurrentUser) {
      return replace(q.translations.Comment.youReactedWith, {
        user: userNamesString,
        reaction: groupedReaction.emoji
      })
    }

    return replace(q.translations.Comment.userReactedWith, {
      user: userNamesString,
      reaction: groupedReaction.emoji
    })
  }

  /**
   * Groups the reactions by emoji
   * @returns {GroupedReaction[]}
   */
  getGroupedReactions(): GroupedReaction[] {
    const groupedReactions: GroupedReaction[] = []

    if (!this.reactions) {
      return groupedReactions
    }

    this.reactions.forEach(reaction => {
      const groupedReaction = groupedReactions.find(r => r.emoji === reaction.emoji)

      if (groupedReaction) {
        groupedReaction.userIds.push(reaction.userId)
      } else {
        groupedReactions.push({
          emoji: reaction.emoji,
          userIds: [reaction.userId]
        })
      }
    })

    return groupedReactions
  }
}
